import _ from 'lodash'
import { EntityState } from '@reduxjs/toolkit'

import { CommonState, Model } from '@/core/classes/Model'
import { SearchOrdersQuery, SearchRequestOptions } from '@/core/types/request'
import { SearchResponse } from '@/core/types/response'

import { Staff } from './staff.model'
import { Patient } from './patient.model'

export enum OrderStatus {
  Accepted = 'Accepted',
  Canceled = 'Canceled',
  Completed = 'Completed',
  Confirmed = 'Confirmed',
  Consultation_Created = 'Consultation_Created',
  In_Progress = 'In_Progress',
  No_Show = 'No_Show',
  Pending = 'Pending',
  Refused = 'Refused',
  Rescheduled = 'Rescheduled',
}

export type Order = {
  _id: string
  acceptedBy?: Staff
  createdBy: Patient | Staff
  receiverId: string
  receiverType: string
  referenceId: string
  status: OrderStatus
  type: string
  createdAt: string
}

export type CreateOrderDto = Omit<Order, '_id' | 'bulkOrder' | 'consultation' | 'doctor' | 'patient' | 'pds' | 'referenceId' | 'status'> & {
  doctor?: string
}

export type UpdateOrderDto = Partial<Omit<CreateOrderDto, 'acceptedBy' | 'createdBy' | 'type'>> & {
  acceptedBy?: string
  status?: OrderStatus
}

export type OrderState = CommonState & {
  orders: EntityState<Order, Order['_id']>
}

class OrderModel extends Model {
  static instance: OrderModel

  static getInstance() {
    if (!OrderModel.instance) {
      OrderModel.instance = new OrderModel('order')
    }
    return OrderModel.instance
  }

  getInitialState(): OrderState {
    return _.merge({}, this.commonState, {
      orders: {
        entities: {},
        ids: [],
      },
    })
  }

  createOrder = async (createOrderDto: CreateOrderDto) => {
    return await this.httpClient.request<Order>('/orders', 'POST', createOrderDto)
  }

  getOrderByReferenceId = async (referenceId: string) => {
    return await this.httpClient.request<Order>(`/orders/reference/${referenceId}`, 'GET')
  }

  listOrders = async (query: SearchOrdersQuery, options?: SearchRequestOptions) => {
    const defaultOptions = { limit: 5, order: 'ASC', skip: 0, orderBy: 'startDate' }
    return await this.httpClient.request<SearchResponse<Order>>('/orders/query', 'POST', { query, options: _.merge({}, defaultOptions, options) })
  }

  updateOrder = async (orderId: string, updateOrderDto: UpdateOrderDto) => {
    return await this.httpClient.request<Order>(`/orders/${orderId}`, 'PATCH', updateOrderDto)
  }
}

export default OrderModel.getInstance()
